import { Injectable } from '@angular/core';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import {
  FilterSportsByTermsAction,
  LoadActiveSportsAction,
  LoadSportDefinitionAction,
  ResetFilterSportDefinitionAction,
  SetBackScreenSportDefinitionAction,
} from './sports.actions';
import { SportDefinition, SportDefinitionResume, SportsService } from '@smatchy/sdk';
import { environment } from '../../../../../environments/environment';
import { tap } from 'rxjs';

function resolveActiveSportsLang(store: Store, translate: TranslateService): 'en' | 'fr' {
  const fromProfile = store.selectSnapshot(
    (s: { userProfile?: { profile?: { language?: string } } }) => s.userProfile?.profile?.language,
  );
  const raw = fromProfile || translate.currentLang || translate.defaultLang || 'fr';
  return raw === 'en' ? 'en' : 'fr';
}

export interface SportsStateModel {
  loadingActiveSports: boolean;
  loadingSingleSport: boolean;
  allActiveSports: SportDefinitionResume[];
  filteredActiveSports: SportDefinitionResume[];
  /** null until a sport is opened, which is what the defaults set. */
  currentSport: SportDefinition | null;
  search: string;

  backScreen: string;
  sportsDefinition: SportDefinition[];
}

const defaults: SportsStateModel = {
  loadingActiveSports: false,
  loadingSingleSport: false,
  allActiveSports: [],
  filteredActiveSports: [],
  currentSport: null,
  backScreen: '',
  search: '',
  sportsDefinition: [],
};

@State<SportsStateModel>({
  name: 'sports',
  defaults,
})
@Injectable()
export class SportsState {
  constructor(
    private readonly _sportsService: SportsService,
    private readonly _store: Store,
    private readonly _translate: TranslateService,
  ) {}

  @Action(LoadActiveSportsAction)
  async loadActiveSports({ patchState, dispatch }: StateContext<SportsStateModel>) {
    patchState({
      loadingActiveSports: true,
    });

    const lang = resolveActiveSportsLang(this._store, this._translate);

    return this._sportsService.getActiveSports({ lang }).pipe(
      tap((res) => {
        // No guard at all before this: an answer with no body threw here, which
        // left `loadingActiveSports` true for good and the list spinning. The
        // flag is cleared either way now, and an empty list is what a screen
        // with nothing to show already handles.
        if (!carriesAValue(res)) {
          patchState({ loadingActiveSports: false });
          return;
        }

        const allSports = res.value.sports;

        const sportActivesVersionApp = allSports.filter((s) => environment.activeSports.includes(s.id));
        patchState({
          loadingActiveSports: false,
          allActiveSports: sportActivesVersionApp || [],
          filteredActiveSports: sportActivesVersionApp || [],
        });
      }),
    );
  }

  @Action(FilterSportsByTermsAction)
  async filterSportsByTermsAction(
    { patchState, getState }: StateContext<SportsStateModel>,
    { payload }: FilterSportsByTermsAction,
  ) {
    const state = getState();
    patchState({
      search: payload.term,
      filteredActiveSports: state.allActiveSports.filter((sport) =>
        sport.tags.some((t) => t.toLowerCase().includes(payload.term.toLowerCase())),
      ),
    });
  }

  @Action(LoadSportDefinitionAction)
  async loadSportDefinitionAction(
    { patchState, getState }: StateContext<SportsStateModel>,
    { sportId }: LoadSportDefinitionAction,
  ) {
    patchState({
      loadingSingleSport: true,
    });
    this._sportsService
      .getSport({
        id: sportId,
      })
      .subscribe((res) => {
        patchState({
          loadingSingleSport: false,
          currentSport: res.value?.sport,
        });
      });
  }

  @Action(ResetFilterSportDefinitionAction)
  async resetFilterSportDefinitionAction({ patchState, getState }: StateContext<SportsStateModel>) {
    patchState({
      search: '',
      filteredActiveSports: getState().allActiveSports,
    });
  }

  @Action(SetBackScreenSportDefinitionAction)
  async setBackScreen(
    { patchState, getState }: StateContext<SportsStateModel>,
    { payload }: SetBackScreenSportDefinitionAction,
  ) {
    patchState({
      backScreen: payload.backScreen,
    });
  }
}
